import { useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

function formatShortDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

// A single bordered, clickable date box for the collapsed view - clicking
// it expands the widget, same as the chevron in the header.
function DateBox({ label, value, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        width: "100%",
        textAlign: "center",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        padding: "6px 8px",
        cursor: "pointer",
        transition: "border-color 0.15s ease, background-color 0.15s ease",
        "&:hover": {
          borderColor: "primary.main",
          backgroundColor: "action.hover",
        },
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

// Mirrors RadioDateRangePicker's controls, but stacked vertically
function FloatingDateRangeWidget(props) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        top: "50%",
        right: 24,
        transform: "translateY(-50%)",
        zIndex: 1200,
        padding: 2,
        width: expanded ? 260 : 130,
        transition: "width 0.15s ease",
        backgroundColor: "#15191c",
        backgroundImage: "none",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center" }}
        >
          Date Range
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? "Collapse date range" : "Expand date range"}
        >
          {expanded ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </IconButton>
      </Box>

      {expanded ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <Switch
                size="small"
                checked={props.getByArrivalDate}
                onChange={(event) => props.onGetByArrivalDateChange(event.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="caption">
                {props.getByArrivalDate ? "Arrival Date" : "Timestamp"}
              </Typography>
            }
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              label="Start Date"
              value={props.startDate}
              onChange={props.onStartDateChange}
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />
            <TimePicker
              label="Start Time"
              value={props.startDate}
              onChange={props.onStartDateChange}
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              label="End Date"
              value={props.endDate}
              onChange={props.onEndDateChange}
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />
            <TimePicker
              label="End Time"
              value={props.endDate}
              onChange={props.onEndDateChange}
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />
          </LocalizationProvider>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <IconButton size="small" onClick={props.onRefresh} aria-label="Refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, mt: 1 }}>
          <DateBox
            label="Start"
            value={formatShortDate(props.startDate)}
            onClick={() => setExpanded(true)}
          />
          <Typography variant="caption" color="text.secondary">
            to
          </Typography>
          <DateBox
            label="End"
            value={formatShortDate(props.endDate)}
            onClick={() => setExpanded(true)}
          />
        </Box>
      )}
    </Paper>
  );
}

export default FloatingDateRangeWidget;
